import { useState, useCallback } from 'react';
import { Resource } from 'types';
import type { ItemType } from 'types';
import bootConfig from 'utils/bootConfig';

export interface RecentEntry {
  id: string;
  url: string;
  title: string;
  /** Stored as the exact ItemType so downstream casts (entry.type as ItemType) are unnecessary. */
  type: ItemType;
  visitCount: number;
  lastVisited: string;
}

// Key is scoped to the logged-in user so different users on the same browser
// each see only their own recently accessed items.
const LS_KEY = `vl_recent_items_${bootConfig.authInfo.idLoggedInWebuser}`;
const MAX_STORED = 50;
const MAX_DISPLAYED = 8;

function readEntries(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as RecentEntry[]) : [];
  } catch {
    return [];
  }
}

function writeEntries(entries: RecentEntry[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(entries));
  } catch {
    // storage full — silently skip
  }
}

export function useRecentResources() {
  const [entries, setEntries] = useState<RecentEntry[]>(readEntries);

  const removeEntry = useCallback((id: string) => {
    setEntries((prev) => {
      const updated = prev.filter((e) => e.id !== id);
      writeEntries(updated);
      return updated;
    });
  }, []);

  const trackVisit = useCallback((resource: Resource) => {
    setEntries((prev) => {
      const existing = prev.find((e) => e.id === resource.id);
      let updated: RecentEntry[];
      if (existing) {
        updated = prev.map((e) =>
          e.id === resource.id
            ? { ...e, visitCount: e.visitCount + 1, lastVisited: new Date().toISOString() }
            : e,
        );
      } else {
        updated = [
          ...prev,
          {
            id: resource.id,
            url: resource.url,
            title: resource.title,
            type: resource.type,
            visitCount: 1,
            lastVisited: new Date().toISOString(),
          },
        ];
      }
      const trimmed = updated
        .sort((a, b) => new Date(b.lastVisited).getTime() - new Date(a.lastVisited).getTime())
        .slice(0, MAX_STORED);
      writeEntries(trimmed);
      return trimmed;
    });
  }, []);

  const topItems = [...entries]
    .sort((a, b) => new Date(b.lastVisited).getTime() - new Date(a.lastVisited).getTime())
    .slice(0, MAX_DISPLAYED);

  return { topItems, trackVisit, removeEntry };
}
